import { useMemo } from 'react';

import { useLocation } from '../routes';
import { useAppStore } from '../store';

export type SmoPropertyScope = 'self-managed' | 'passive-owned';

/** Merge caller filters with SMO property scope (when applicable). */
export function mergeSmoPropertyScopeFilters(
  base?: Record<string, string>,
  smo?: Record<string, string>,
): Record<string, string> | undefined {
  if (!base && !smo) return undefined;
  return { ...(base ?? {}), ...(smo ?? {}) };
}

export function useSmoMergedPropertyScopeFilters(
  base?: Record<string, string>,
): Record<string, string> | undefined {
  const smo = useSmoPropertyScopeFilters();
  return useMemo(() => mergeSmoPropertyScopeFilters(base, smo), [base, smo]);
}

/** API filter for mixed SMO property lists (undefined for non-SMO users). */
export function useSmoPropertyScopeFilters(): Record<string, string> | undefined {
  const scope = useSmoPropertyScope();
  return useMemo(() => (scope ? { propertyScope: scope } : undefined), [scope]);
}

/**
 * Resolves SMO list scope from query param or route.
 * Ops `/app/*` routes default to self-managed; passive owner portal routes use passive-owned.
 */
export function useSmoPropertyScope(): SmoPropertyScope | undefined {
  const isSelfManagingOwner = useAppStore((s) => s.isSelfManagingOwner);
  const location = useLocation();

  return useMemo(() => {
    if (!isSelfManagingOwner) return undefined;

    const params = new URLSearchParams(location.search);
    const explicit = params.get('propertyScope');
    if (explicit === 'passive-owned' || explicit === 'self-managed') {
      return explicit;
    }

    const path = location.pathname;
    if (
      path.startsWith('/app/owner/') ||
      path === '/app/maintenance/completed'
    ) {
      return 'passive-owned';
    }

    return 'self-managed';
  }, [isSelfManagingOwner, location.pathname, location.search]);
}

export function useSmoIsPassiveView(): boolean {
  return useSmoPropertyScope() === 'passive-owned';
}
